import type { Product } from '../config/homepage'
import { normalizedProductIdForSnapshot } from '../../shared/productIdentity'

export const CART_STORAGE_KEY = 'sg.cart.v2'
export const LEGACY_CART_STORAGE_KEY = 'sg.cart.v1'
export const ORDER_BUILDER_STORAGE_KEY = 'sg.order.builder.v1'
export const CART_CHANGE_EVENT = 'sg:cart-change'

export type CartSelection = {
  productId: string
  productName: string
  category: Product['category']
  image: string
  presentation: string
  quantity: number
}

export type CartBox = {
  id: string
  quantity: number
  price: number
  currency: 'NZD'
  selections: CartSelection[]
  createdAt: string
}

export type CartChangeDetail = {
  type: 'add' | 'replace' | 'remove' | 'clear' | 'replace-all'
  boxes: CartBox[]
  boxId?: string
}

type StoredCartV2 = {
  version: 2
  boxes: CartBox[]
}

type StoredBuilderDraft = {
  version: 1
  packSize: number
  slotProductIds: string[]
  updatedAt: string
}

export type RestoredBuilderState = {
  packSize: number
  slots: string[]
}

function storage(): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function activeEditBoxId(): string | null {
  if (typeof window === 'undefined' || !/^\/order\/?$/.test(window.location.pathname)) return null
  return new URLSearchParams(window.location.search).get('edit')
}

function emitCartChange(detail: CartChangeDetail) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent<CartChangeDetail>(CART_CHANGE_EVENT, { detail }))
}

function isProductCategory(value: unknown): value is Product['category'] {
  return value === 'Brownie Cookie Sandwich' || value === 'Stuffed Cookie' || value === 'Brownie'
}

function isCartSelection(value: unknown): value is CartSelection {
  if (!value || typeof value !== 'object') return false
  const selection = value as Partial<CartSelection>
  return typeof selection.productId === 'string'
    && selection.productId.length > 0
    && typeof selection.productName === 'string'
    && isProductCategory(selection.category)
    && typeof selection.image === 'string'
    && typeof selection.presentation === 'string'
    && Number.isInteger(selection.quantity)
    && Number(selection.quantity) > 0
}

function isCartBox(value: unknown): value is CartBox {
  if (!value || typeof value !== 'object') return false
  const box = value as Partial<CartBox>
  if (typeof box.id !== 'string'
    || !Number.isInteger(box.quantity)
    || Number(box.quantity) <= 0
    || typeof box.price !== 'number'
    || !Number.isFinite(box.price)
    || box.price < 0
    || box.currency !== 'NZD'
    || !Array.isArray(box.selections)
    || typeof box.createdAt !== 'string'
    || !box.selections.every(isCartSelection)) {
    return false
  }

  return box.selections.reduce((total, selection) => total + selection.quantity, 0) === box.quantity
}

function parseBoxes(raw: string | null, expectedVersion: 1 | 2): CartBox[] | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as { version?: unknown; boxes?: unknown }
    if (parsed.version !== expectedVersion || !Array.isArray(parsed.boxes)) return null
    return parsed.boxes.filter(isCartBox)
  } catch {
    return null
  }
}

function writeCartBoxes(boxes: CartBox[]) {
  const store = storage()
  if (!store) return
  const payload: StoredCartV2 = { version: 2, boxes }
  try {
    store.setItem(CART_STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // Keep the active interaction usable when persistent storage is unavailable.
  }
}

export function readCartBoxes(): CartBox[] {
  const store = storage()
  if (!store) return []

  const current = parseBoxes(store.getItem(CART_STORAGE_KEY), 2)
  if (current) return current

  // One-way compatibility read for carts created before the v2 store existed.
  // The legacy key is deliberately not deleted: persistence must never clear a
  // customer's cart as a side effect of a schema migration.
  const legacy = parseBoxes(store.getItem(LEGACY_CART_STORAGE_KEY), 1)
  if (legacy) {
    writeCartBoxes(legacy)
    return legacy
  }

  return []
}

export function migrateCartProductIdsForCatalogue(
  catalogue: ReadonlyArray<{
    id: string
    name: string
    type?: CartSelection['category']
    image?: string
    hoverBackground?: string
  }>,
): CartBox[] {
  const boxes = readCartBoxes()
  if (!boxes.length || !catalogue.length) return boxes

  const byId = new Map(catalogue.map((product) => [product.id, product]))
  const normalizedName = (value: string) => value.replace(/\s+/g, ' ').trim().toLocaleLowerCase('en-NZ')
  let changed = false

  const migrated = boxes.map((box) => ({
    ...box,
    selections: box.selections.map((selection) => {
      if (byId.has(selection.productId)) return selection

      const nextProductId = normalizedProductIdForSnapshot(selection.productId, selection.productName)
      if (nextProductId === selection.productId) return selection
      const currentProduct = byId.get(nextProductId)
      if (!currentProduct || normalizedName(currentProduct.name) !== normalizedName(selection.productName)) {
        return selection
      }

      changed = true
      return {
        ...selection,
        productId: nextProductId,
        category: currentProduct.type ?? selection.category,
        image: currentProduct.image ?? selection.image,
        presentation: currentProduct.hoverBackground ?? selection.presentation,
      }
    }),
  }))

  if (changed) writeCartBoxes(migrated)
  return migrated
}

export function cartBoxCount(): number {
  return readCartBoxes().length
}

export function cartCookieCount(boxes: readonly CartBox[] = readCartBoxes()): number {
  return boxes.reduce((total, box) => total + box.quantity, 0)
}

export function expandCartBoxSelections(box: CartBox): string[] {
  return box.selections.flatMap((selection) => (
    Array.from({ length: selection.quantity }, () => selection.productId)
  ))
}

export function groupSelectedProducts(slotProductIds: string[], catalogue: Product[]): CartSelection[] {
  const counts = new Map<string, number>()
  for (const productId of slotProductIds) counts.set(productId, (counts.get(productId) ?? 0) + 1)

  return catalogue.flatMap((product) => {
    const quantity = counts.get(product.id) ?? 0
    if (!quantity) return []
    return [{
      productId: product.id,
      productName: product.name,
      category: product.category,
      image: product.image,
      presentation: product.presentation,
      quantity,
    } satisfies CartSelection]
  })
}

export function createCartBox(slotProductIds: string[], catalogue: Product[], quantity: number, price: number): CartBox {
  if (slotProductIds.length !== quantity) throw new Error('Cannot commit an incomplete box.')

  const selections = groupSelectedProducts(slotProductIds, catalogue)
  const selectedQuantity = selections.reduce((total, selection) => total + selection.quantity, 0)
  if (selectedQuantity !== quantity) throw new Error('Cannot commit a box containing unavailable products.')

  return {
    id: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `box-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    quantity,
    price,
    currency: 'NZD',
    selections,
    createdAt: new Date().toISOString(),
  }
}

export function addCartBox(box: CartBox): CartBox[] {
  const boxes = readCartBoxes()
  const editBoxId = activeEditBoxId()

  if (editBoxId) {
    const targetIndex = boxes.findIndex((item) => item.id === editBoxId)
    if (targetIndex < 0) return boxes

    const original = boxes[targetIndex]
    const replacement: CartBox = {
      ...box,
      id: original.id,
      createdAt: original.createdAt,
    }
    const nextBoxes = boxes.map((item, index) => index === targetIndex ? replacement : item)
    writeCartBoxes(nextBoxes)
    emitCartChange({ type: 'replace', boxes: nextBoxes, boxId: original.id })
    return nextBoxes
  }

  const nextBoxes = [...boxes, box]
  writeCartBoxes(nextBoxes)
  emitCartChange({ type: 'add', boxes: nextBoxes, boxId: box.id })
  return nextBoxes
}

export function removeCartBox(boxId: string): CartBox[] {
  const boxes = readCartBoxes()
  const nextBoxes = boxes.filter((box) => box.id !== boxId)
  if (nextBoxes.length === boxes.length) return boxes
  writeCartBoxes(nextBoxes)
  emitCartChange({ type: 'remove', boxes: nextBoxes, boxId })
  return nextBoxes
}

export function replaceCartBoxes(boxes: CartBox[]): CartBox[] {
  const safeBoxes = boxes.filter(isCartBox)
  writeCartBoxes(safeBoxes)
  emitCartChange({ type: 'replace-all', boxes: safeBoxes })
  return safeBoxes
}

export function clearCartBoxes(): void {
  const store = storage()
  if (!store) return
  try {
    store.removeItem(CART_STORAGE_KEY)
    store.removeItem(LEGACY_CART_STORAGE_KEY)
  } catch {
    // Explicit clear is best-effort if browser storage is unavailable.
  }
  emitCartChange({ type: 'clear', boxes: [] })
}

function readBuilderDraft(): StoredBuilderDraft | null {
  const store = storage()
  if (!store) return null
  try {
    const parsed = JSON.parse(store.getItem(ORDER_BUILDER_STORAGE_KEY) ?? '') as Partial<StoredBuilderDraft>
    if (parsed.version !== 1
      || !Number.isInteger(parsed.packSize)
      || Number(parsed.packSize) <= 0
      || !Array.isArray(parsed.slotProductIds)
      || !parsed.slotProductIds.every((productId) => typeof productId === 'string')
      || typeof parsed.updatedAt !== 'string') {
      return null
    }
    return parsed as StoredBuilderDraft
  } catch {
    return null
  }
}

export function restoreOrderBuilderState({
  allowedPackSizes,
  validProductIds,
  defaultPackSize,
}: {
  allowedPackSizes: readonly number[]
  validProductIds: readonly string[]
  defaultPackSize: number
}): RestoredBuilderState {
  const allowedPacks = new Set(allowedPackSizes)
  const validProducts = new Set(validProductIds)
  const fallbackPackSize = allowedPacks.has(defaultPackSize)
    ? defaultPackSize
    : allowedPackSizes[0] ?? defaultPackSize
  const editBoxId = activeEditBoxId()

  if (editBoxId) {
    const box = readCartBoxes().find((item) => item.id === editBoxId)
    const slots = box ? expandCartBoxSelections(box) : []
    if (box
      && allowedPacks.has(box.quantity)
      && slots.length === box.quantity
      && slots.every((productId) => validProducts.has(productId))) {
      return { packSize: box.quantity, slots }
    }

    // Leave the original committed box untouched if it can no longer be safely
    // restored into the current builder. Customer-facing stale-cart resolution
    // is intentionally left to owner-approved Visual QA.
    return { packSize: fallbackPackSize, slots: [] }
  }

  const draft = readBuilderDraft()
  if (!draft) return { packSize: fallbackPackSize, slots: [] }

  const packSize = allowedPacks.has(draft.packSize) ? draft.packSize : fallbackPackSize
  const slots = draft.slotProductIds
    .filter((productId) => validProducts.has(productId))
    .slice(0, packSize)

  // Rewrite a stale draft in sanitized form so removed weekly flavours or old
  // pack sizes cannot repeatedly re-enter the builder on future visits.
  if (packSize !== draft.packSize || slots.length !== draft.slotProductIds.length) {
    saveOrderBuilderState(packSize, slots)
  }

  return { packSize, slots }
}

export function saveOrderBuilderState(packSize: number, slots: string[]): void {
  const store = storage()
  if (!store || activeEditBoxId() || !Number.isInteger(packSize) || packSize <= 0) return

  const payload: StoredBuilderDraft = {
    version: 1,
    packSize,
    slotProductIds: slots.filter((productId) => typeof productId === 'string'),
    updatedAt: new Date().toISOString(),
  }

  try {
    store.setItem(ORDER_BUILDER_STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // The builder remains usable when browser storage is unavailable.
  }
}

export function clearOrderBuilderState(): void {
  const store = storage()
  if (!store) return
  try {
    store.removeItem(ORDER_BUILDER_STORAGE_KEY)
  } catch {
    // Explicit draft clearing is best-effort if browser storage is unavailable.
  }
}
